import React, { useState, useEffect, useCallback } from 'react';
import Plot from 'react-plotly.js';
import './ExplainabilityContainer.css';
import ContainerBox from './ContainerBox';

const ExplainabilityContainer = ({
  analysis,
  visible,
  selectedTarget,
  droppedColumns,
  onReadyClick
}) => {
  const [shapData, setShapData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showShapPlot, setShowShapPlot] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  // Poll getShapStatus every 3s while a background calculation is running
  useEffect(() => {
    if (!isPolling || !analysis?.datasets?.[0]?.id || !selectedTarget?.name) return;
    const interval = setInterval(async () => {
      try {
        const { apiService } = await import('../services/api');
        const response = await apiService.getShapStatus(
          analysis.datasets[0].id, selectedTarget.name
        );
        const { shap_result, shap_status } = response.data;
        if (shap_status === 'ready' || shap_status === 'stale') {
          if (shap_result) setShapData(shap_result);
          setIsStale(shap_status === 'stale');
          setIsPolling(false);
          setLoading(false);
        } else if (shap_status === 'error') {
          setError('SHAP calculation failed. Please try again.');
          setIsPolling(false);
          setLoading(false);
        }
      } catch (err) {
        console.error('SHAP status polling error:', err);
      }
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPolling, analysis?.datasets?.[0]?.id, selectedTarget?.name]);

  const handleCalculateShap = useCallback(async () => {
    if (!analysis?.datasets?.[0]?.id || !selectedTarget?.name) return;

    setLoading(true);
    setError(null);

    try {
      const { apiService } = await import('../services/api');
      const response = await apiService.calculateShap(
        analysis.datasets[0].id,
        selectedTarget.name
      );
      if (response.status === 202) {
        setIsPolling(true);
        return; // loading stays true; polling will clear it
      }
      setShapData(response.data.result);
      setLoading(false);
    } catch (err) {
      setError('Failed to calculate SHAP values. Please try again.');
      console.error('SHAP calculation error:', err);
      setLoading(false);
    }
  }, [analysis?.datasets, selectedTarget?.name]);

  const loadShapData = useCallback(async () => {
    if (!analysis?.datasets?.[0]?.id || !selectedTarget?.name) return;

    try {
      const { apiService } = await import('../services/api');
      const response = await apiService.getShapStatus(
        analysis.datasets[0].id,
        selectedTarget.name
      );

      const { shap_result, shap_status } = response.data;

      if (shap_status === 'pending') {
        setLoading(true);
        setIsPolling(true);
        return;
      }

      // Check if artifact is stale
      const hasStale = shap_status === 'stale';
      setIsStale(hasStale);

      // Always load results even if stale, so user can see old results until they refresh
      if (shap_result) {
        setShapData(shap_result);
      } else if (!shap_result && !hasStale) {
        // No existing data and not stale, automatically trigger calculation
        handleCalculateShap();
      }
    } catch (err) {
      console.error('Failed to load SHAP data:', err);
      // Don't show error to user, just fail silently
    }
  }, [analysis?.datasets, selectedTarget?.name, handleCalculateShap]);

  const handleRefresh = useCallback(async () => {
    if (!analysis?.datasets?.[0]?.id || !selectedTarget?.name) return;

    setRefreshing(true);
    setError(null);

    try {
      const { apiService } = await import('../services/api');

      // Refresh explainability artifacts (calculates SHAP values)
      await apiService.refreshExplainabilityArtifacts(
        analysis.datasets[0].id,
        selectedTarget.name
      );

      // Reload results
      await loadShapData();

      // Clear stale flag
      setIsStale(false);
    } catch (err) {
      setError('Failed to refresh explainability artifacts. Please try again.');
      console.error('Refresh error:', err);
    } finally {
      setRefreshing(false);
    }
  }, [analysis?.datasets, selectedTarget?.name, loadShapData]);

  useEffect(() => {
    if (visible && analysis?.datasets?.[0]?.id && selectedTarget?.name) {
      // Reset state when target changes
      setShapData(null);
      setIsStale(false);
      setError(null);
      setIsPolling(false);

      // Load SHAP data for the new target
      loadShapData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, analysis?.datasets, selectedTarget?.name]);

  // Re-check status when columns are dropped/added
  useEffect(() => {
    if (visible && analysis?.datasets?.[0]?.id && selectedTarget?.name) {
      loadShapData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [droppedColumns]);

  const createShapPlot = () => {
    if (!shapData) return null;

    const { feature_names, shap_values, feature_values } = shapData;

    // Calculate mean absolute SHAP value for each feature
    const meanAbsShap = feature_names.map((_, featureIdx) => {
      const absValues = shap_values.map(sample => Math.abs(sample[featureIdx]));
      return absValues.reduce((a, b) => a + b, 0) / absValues.length;
    });

    // Sort features by importance
    const sortedIndices = meanAbsShap
      .map((value, index) => ({ value, index }))
      .sort((a, b) => b.value - a.value)
      .map(item => item.index);

    // Take top 10 features
    const topN = Math.min(10, feature_names.length);
    const topNIsAllFeatures = feature_names.length <= 10;
    const topIndices = sortedIndices.slice(0, topN);

    // Prepare data for beeswarm-style plot
    const traces = topIndices.map(featureIdx => {
      const featureName = feature_names[featureIdx];
      const shapVals = shap_values.map(sample => sample[featureIdx]);
      const featureVals = feature_values.map(sample => sample[featureIdx]);

      return {
        x: shapVals,
        y: Array(shapVals.length).fill(featureName),
        mode: 'markers',
        type: 'scatter',
        name: featureName,
        marker: {
          size: 8,
          color: featureVals,
          colorscale: 'RdBu',
          showscale: featureIdx === topIndices[0], // Show colorbar only for first trace
          colorbar: {
            title: 'Feature Value',
            titleside: 'right'
          },
          line: {
            color: 'rgba(0,0,0,0.3)',
            width: 0.5
          }
        },
        text: featureVals.map((val, idx) => 
          `Feature Value: ${val.toFixed(2)}<br>SHAP Value: ${shapVals[idx].toFixed(4)}`
        ),
        hovertemplate: '%{text}<extra></extra>',
        showlegend: false
      };
    });

    const layout = {
      title: {
        text: topNIsAllFeatures
          ? 'SHAP Feature Importance'
          : `SHAP Feature Importance (Top ${topN} Features)`,
        font: { size: 16, weight: 600 }
      },
      xaxis: {
        title: 'SHAP Value (impact on model output)',
        zeroline: true,
        zerolinewidth: 2,
        zerolinecolor: 'rgba(0,0,0,0.3)'
      },
      yaxis: {
        title: '',
        automargin: true
      },
      height: 500,
      margin: { l: 150, r: 100, t: 60, b: 60 },
      hovermode: 'closest',
      plot_bgcolor: 'rgba(250,250,250,1)',
      paper_bgcolor: 'white'
    };

    const config = {
      responsive: true,
      displayModeBar: true,
      displaylogo: false,
      modeBarButtonsToRemove: ['lasso2d', 'select2d']
    };

    return <Plot data={traces} layout={layout} config={config} style={{ width: '100%' }} />;
  };

  if (!visible) return null;

  return (
    <ContainerBox
      title="Understand the Model"
      isReady={shapData && !loading && !refreshing}
      scrollToNext={false}
      readyLabel='Actionable Insights'
      isStale={isStale}
      onRefreshClick={handleRefresh}
      refreshing={refreshing}
      onReadyClick={onReadyClick}
    >
      {error && (
        <div className="explainability-error">
          <p>{error}</p>
        </div>
      )}

      {/* Show loading state when refreshing */}
      {refreshing && (
        <div className="explainability-loading">
          <div className="loading-spinner"></div>
          <p>Refreshing SHAP values...</p>
        </div>
      )}

      {loading && !refreshing && (
        <div className="explainability-loading">
          <div className="loading-spinner"></div>
          <p>Calculating SHAP values...</p>
        </div>
      )}


      {shapData && !loading && (
        <div className="explainability-results">
          {/* Key Insights Section */}
          {shapData.relationships && shapData.relationships.length > 0 && (
            <div className="explainability-insights">
              <p className="explainability-insights__description">
                These are the clearest relationships between features and {selectedTarget?.name || 'the target'}, ordered by importance
              </p>
              <ul className="explainability-insights__list">
                {shapData.relationships.map((relationship, idx) => (
                  <li key={idx} className="explainability-insights__item">
                    {relationship}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Toggle button for SHAP plot */}
          <button
            className="explainability-toggle-button"
            onClick={() => setShowShapPlot(!showShapPlot)}
          >
            <span>
              {showShapPlot ? '▼' : '▶'} {showShapPlot ? 'Hide' : 'Show'} Detailed SHAP Analysis
            </span>
          </button>

          {/* Collapsible SHAP plot section */}
          {showShapPlot && (
            <>
              <div className="explainability-info">
                <p className="explainability-info__text">
                  This plot shows the impact of each feature on the model's predictions.
                  Features are ranked by their average absolute SHAP value (importance).
                  Each dot represents a sample, colored by the feature's value (red = high, blue = low).
                </p>
                <div className="explainability-stats">
                  <div className="explainability-stat">
                    <span className="explainability-stat__label">Samples Analyzed:</span>
                    <span className="explainability-stat__value">{shapData.n_samples}</span>
                  </div>
                  <div className="explainability-stat">
                    <span className="explainability-stat__label">Base Value:</span>
                    <span className="explainability-stat__value">{shapData.base_value.toFixed(4)}</span>
                  </div>
                  <div className="explainability-stat">
                    <span className="explainability-stat__label">Features:</span>
                    <span className="explainability-stat__value">{shapData.feature_names.length}</span>
                  </div>
                </div>
              </div>

              <div className="explainability-plot">
                {createShapPlot()}
              </div>
            </>
          )}
        </div>
      )}
    </ContainerBox>
  );
};

export default ExplainabilityContainer;

